import { Component } from '@angular/core';
import { TimelineMax, Power2, Quad } from 'gsap';
import * as CustomWiggle from '../../../assets/gsap/plugins/CustomWiggle';
import * as CustomBounce from '../../../assets/gsap/plugins/CustomBounce';
import { CbsBasicsComponent } from '../animation-base-classes/cbs-basics.component';
import { IconService } from '../../core/icon.service';
import { params as paramsSocial } from './social.params';
import * as _ from 'lodash';
import * as d3 from 'd3';

@Component({
  selector: 'app-demo-tilastokeskus-line',
  templateUrl: './demo-tilastokeskus-line.component.html',
  styleUrls: ['./demo-tilastokeskus-line.component.scss']
})
export class DemoTilastokeskusLineComponent extends CbsBasicsComponent {

  public valuesCalculated = false;
  public scaleX;
  public scaleY;
  public guidesGroup;
  public backgroundGroup;
  public titleGroup;
  public xAxisGroup;
  public yAxisGroup;
  public chartGroup;
  public legendGroup;
  public mouseoverGroup;
  public legendRows;
  public legendColumns;
  public legendLabels;
  public indices;
  public dataValues;
  public zeroY = 0;
  public wiggly = false;
  public yAxisLabelWidth;
  public sizes;
  public colors = ['#0073B0', '#A60085', '#C0D730', '#F8941F', '#32C1BA', '#E21875'];

  constructor(
    public iconService: IconService,
  ) {
    super(iconService);
    this.paramObject = {
      'social': paramsSocial,
    };
  }

  public async calculateValues() {
    super.calculateValues();
    let g = (k): any => this.values[k];
    let s = (k, v): void => this.values[k] = v;

    // Copy all parameters to values
    this.values = _.clone(this.parameters);
    if (!this.values['size']) {
      this.values['size'] = { 'value': [this.values['width'], this.values['height']] };
    }
    this.width = this.values['size']['value'][0];
    this.height = this.values['size']['value'][1];
    // Reset the fontList
    this.fontList = _.clone(this.standardFontList);
    // Add the icon font to the fontlist
    this.fontList.push((this.values['icon'] || {})['font']);
    // Sort out the legend
    this.legendLabels = _.drop(this.data[0], this.params['data']['rowLabelNumber'] || 0);
    this.legendColumns = g('legendColumns') || 1;
    this.legendRows = _.ceil(this.legendLabels.length / this.legendColumns);

    // Figure out Y-axis label widths
    let tempSVG = d3.select(this.tempRef.nativeElement).select('#temp');
    tempSVG.selectAll('*').remove();
    this.sizes = await this.getAllSizes(tempSVG, this.values);
    this.yAxisLabelWidth = this.sizes['yAxisLabelWidth'].width;
    s('labelSpaceY', this.yAxisLabelWidth + g('yAxisLabelOffset'))

    // titleSpace
    s('tS-X', g('marginLeft'));
    s('tS-Y', g('marginTop'));
    s('tS-W', g('width') - g('marginLeft') - g('marginRight'));
    let singleReturn = /\r\n|\n|\f/g;
    let titleLines = g('titleText').split(singleReturn);
    let tn = titleLines.length;
    let subTitleLines = g('subTitleText').split(singleReturn);
    let stn = subTitleLines.length;

    let height = g('titleText').length === 0 && g('subTitleText').length === 0
      ? 0
      : tn * g('titleFontSize') + (tn - 1) * (g('titleLineHeight') - 1) * g('titleFontSize') + g('titleMarginBottom') + stn * g('subTitleFontSize') + (stn - 1) * (g('subTitleLineHeight') - 1) * g('subTitleFontSize') + g('subTitleMarginBottom');
    s('tS-H', height);

    // unitSpace
    let unitHeight = g('unitText') ? g('unitFontSize') + g('unitMarginTop') + g('unitMarginBottom') : 0;
    s('uS-X', g('tS-X'));
    s('uS-Y', g('tS-Y') + g('tS-H'));
    s('uS-W', g('tS-W'));
    s('uS-H', g('unitMarginTop') + g('unitFontSize') + g('unitMarginBottom'));

    // sourceSpace
    let sourceHeight = g('sourceText') ? g('sourceFontSize') + g('sourceMargin') : 0;

    // legendSpace
    s('lS-W', g('width') - g('marginLeft') - g('labelSpaceY') - g('marginRight'));
    s('lS-H', g('legendMarginTop') + this.legendRows * g('legendRowHeight') + (this.legendRows - 1) * g('legendVerticalSpacing'));
    s('lS-X', g('marginLeft') + g('labelSpaceY'))
    s('lS-Y', g('height') - g('marginBottom') - sourceHeight - g('lS-H'))
    // labelSpaceY
    s('lSY-X', g('marginLeft'));
    s('lSY-Y', g('marginTop') + g('tS-H') + unitHeight);
    s('lSY-W', g('labelSpaceY'));
    s('lSY-H', g('height') - g('marginTop') - g('tS-H') - unitHeight - g('lS-H') - g('labelSpaceX') - sourceHeight - g('marginBottom'));
    // labelSpaceX
    s('lSX-X', g('marginLeft') + g('labelSpaceY'));
    s('lSX-Y', g('height') - g('marginBottom') - sourceHeight - g('lS-H') - g('labelSpaceX'));
    s('lSX-W', g('width') - g('marginLeft') - g('labelSpaceY') - g('marginRight'));
    s('lSX-H', g('labelSpaceX'));
    // chartPane
    s('cP-X', g('lSX-X'));
    s('cP-Y', g('lSY-Y'));
    s('cP-W', g('lS-W'));
    s('cP-H', g('lSY-H'));
    // lineMargin
    s('cM', g('chartMargin') || (g('cP-W') / this.getDataValues().length) / 2);
    // chartSpace
    s('cS-X', g('cP-X') + g('cM'));
    s('cS-Y', g('cP-Y'));
    s('cS-W', g('cP-W') - 2 * g('cM'));
    s('cS-H', g('cP-H'));
    // barSizing
    let values = this.getDataValues();
    s('categories', values.length);
    s('subCategories', (values[0] || []).length);
    let w = g('cS-W') / (g('categories') - 1);
    s('categoryWidth', w * g('categoryMarginRatio'));
    s('categorySpaceWidth', w * (1 - g('categoryMarginRatio')));
    let sw = g('categoryWidth');
    s('subCategoryWidth', g('subCategories') === 1 ? g('categoryWidth') : g('categoryWidth') * g('subCategoryMarginRatio') / g('subCategories'));
    s('subCategorySpaceWidth', g('subCategories') === 1 ? 0 : g('categoryWidth') * (1 - g('subCategoryMarginRatio')) / (g('subCategories') - 1));
    this.createLineScales();
    this.valuesCalculated = true;
  }

  public async getAllSizes(selection, values) {
    let that = this;
    let promise = new Promise(function(resolve, reject) {
      let observer = new MutationObserver((mutationsList) => {
        let sizes = {
          'yAxisLabelWidth': yAxisLabels.node().getBBox(),
        };
        observer.disconnect();
        resolve(sizes);
      });
      observer.observe(selection.node(), { childList: true });
      let yAxisLabels = that.drawYAxisStepsForMeasurement(selection);
    });
    return promise;
  }

  public createLineScales() {
    let g = (k): any => this.values[k];

    this.dataValues = this.getDataValues();

    // Whatever the size of the rows array only take the first column
    let list = _.map(this.dataValues, a => a[0]);
    this.indices = _.range(list.length);

    // Start and end x-coordinates [x1, x2]
    let rangeX = [g('cS-X'), g('cS-X') + g('cS-W')];
    // Ordinal scale in steps without band width
    this.scaleX = d3.scalePoint()
      // min and max value from the data (source)
      .domain(this.indices)
      // values in the plot to map the domain to (dest)
      .range(rangeX);

    // Start and end y-coordinates [y1, y2] NB y1 > y2
    let rangeY = [g('cS-Y') + g('cS-H'), g('cS-Y')];

    // When the Y-axis does not start or end at zero
    // zero is still drawn as a seperate step
    // with a wiggly line, this is acounted for below
    let minY = g('minY');
    let maxY = g('maxY')
    let stepY = g('stepY');
    // Disabled on purpose, no extra space nor wiggly lines drawn
    // if (minY > 0) {
    //   minY = minY - stepY;
    //   this.zeroY = minY;
    //   this.wiggly = true;
    //   if (this.zeroY < 0) {
    //     this.zeroY = 0;
    //   }
    // } else if (maxY < 0) {
    //   maxY = maxY - stepY;
    //   this.zeroY = maxY;
    //   this.wiggly = true;
    //   if (this.zeroY > 0) {
    //     this.zeroY = 0;
    //   }
    // } else {
    //   this.zeroY = 0;
    //   this.wiggly = false;
    // }

    this.scaleY = d3.scaleLinear()
      .domain([minY, maxY])
      .range(rangeY);
  }

  public async createElements() {
    // Draw guides
    this.guidesGroup = this.host.select('#guides');
    this.guidesGroup.selectAll('*').remove();
    // this.drawGuides(this.guidesGroup);
    // Draw background
    this.backgroundGroup = this.host.select('#background');
    this.backgroundGroup.selectAll('*').remove();
    this.drawBackground(this.backgroundGroup);
    // Draw title
    this.titleGroup = this.host.select('#title');
    this.titleGroup.selectAll('*').remove();
    this.drawTitleText(this.titleGroup);
    // Draw x axis
    this.xAxisGroup = this.host.select('#xAxis');
    this.xAxisGroup.selectAll('*').remove();
    this.drawXAxisBackground(this.xAxisGroup);
    // this.drawXAxisMarkers(this.xAxisGroup);
    this.drawXAxisMarkersMultiPeriod(this.xAxisGroup);
    // Draw y axis
    this.yAxisGroup = this.host.select('#yAxis');
    this.yAxisGroup.selectAll('*').remove();
    this.drawYAxisSteps(this.yAxisGroup);
    this.drawUnitText(this.yAxisGroup);
    // Draw lines
    this.chartGroup = this.host.select('#chart');
    this.chartGroup.selectAll('*').remove();
    this.drawLines(this.chartGroup);
    // Draw legend
    this.legendGroup = this.host.select('#legend');
    this.legendGroup.selectAll('*').remove();
    this.drawLegend(this.legendGroup, 'line');
    this.drawSourceText(this.legendGroup);
  }

  public async createAnimation(noAnimation: boolean = false) {
    let main = new TimelineMax({ repeat: -1 });

    this.animation = main;
    return this.animation;
  }

  public drawGuides(selection) {
    let g = (k): any => this.values[k];

    let singleReturn = /\r\n|\n|\f/g;
    let n, x, xx, y, w, incr;

    // Margins
    let groupMargins = selection.append('g')
      .attr('id', `guide-group-margins`);
    groupMargins.append('rect')
      .attr('id', `guide-entire-image-${this.id}`)
      .attr('x', 0)
      .attr('y', 0)
      .attr('width', g('width'))
      .attr('height', g('height'))
      .attr('fill', '#f7f7f7');
    groupMargins.append('rect')
      .attr('id', `guide-margin-top-${this.id}`)
      .attr('x', 0)
      .attr('y', 0)
      .attr('width', g('width'))
      .attr('height', g('marginTop'))
      .attr('opacity', 0.5)
      .attr('fill', '#92c255');
    groupMargins.append('rect')
      .attr('id', `guide-margin-right-${this.id}`)
      .attr('x', g('width') - g('marginRight'))
      .attr('y', 0)
      .attr('width', g('marginRight'))
      .attr('height', g('height'))
      .attr('opacity', 0.5)
      .attr('fill', '#92c255');
    groupMargins.append('rect')
      .attr('id', `guide-margin-bottom-${this.id}`)
      .attr('x', 0)
      .attr('y', g('height') - g('marginBottom'))
      .attr('width', g('width'))
      .attr('height', g('marginBottom'))
      .attr('opacity', 0.5)
      .attr('fill', '#92c255');
    groupMargins.append('rect')
      .attr('id', `guide-margin-left-${this.id}`)
      .attr('x', 0)
      .attr('y', 0)
      .attr('width', g('marginLeft'))
      .attr('height', g('height'))
      .attr('opacity', 0.5)
      .attr('fill', '#92c255');
    // TitleSpace
    let groupTitle = selection.append('g')
      .attr('id', `guide-group-title-${this.id}`);
    groupTitle.append('rect')
      .attr('id', `guide-title-${this.id}`)
      .attr('x', g('tS-X'))
      .attr('y', g('tS-Y'))
      .attr('width', g('tS-W'))
      .attr('height', g('tS-H'))
      .attr('opacity', 0.5)
      .attr('fill', '#e54048');
    let titleLines = g('titleText').split(singleReturn);
    let tn = titleLines.length;
    y = g('tS-Y');
    if (g('titleText').length !== 0) {
      for (let i = 0; i < tn; i++) {
        groupTitle.append('rect')
          .attr('id', `guide-title-line-${i}-${this.id}`)
          .attr('x', g('tS-X'))
          .attr('y', y)
          .attr('width', g('tS-W'))
          .attr('height', g('titleFontSize'))
          .attr('opacity', 0.25)
          .attr('fill', '#e54048');
        y = y + g('titleLineHeight') * g('titleFontSize');
      }
    }
    let subTitleLines = g('subTitleText').split(singleReturn);
    let stn = subTitleLines.length;
    y = y + g('titleMarginBottom');
    if (g('subTitleText').length !== 0) {
      for (let i = 0; i < stn; i++) {
        groupTitle.append('rect')
          .attr('id', `guide-subtitle-line-${i}-${this.id}`)
          .attr('x', g('tS-X'))
          .attr('y', y)
          .attr('width', g('tS-W'))
          .attr('height', g('subTitleFontSize'))
          .attr('opacity', 0.25)
          .attr('fill', '#e54048');
        y = y + g('subTitleLineHeight') * g('subTitleFontSize');
      }
    }
    // UnitSpace
    let groupUnit = selection.append('g')
      .attr('id', `guide-group-unit-${this.id}`);
    groupUnit.append('rect')
      .attr('id', `guide-unit-${this.id}`)
      .attr('x', g('uS-X'))
      .attr('y', g('uS-Y'))
      .attr('width', g('uS-W'))
      .attr('height', g('uS-H'))
      .attr('opacity', 0.5)
      .attr('fill', '#68afca');
    groupUnit.append('rect')
      .attr('id', `guide-unit-line-${this.id}`)
      .attr('x', g('uS-X'))
      .attr('y', g('uS-Y') + g('unitMarginTop'))
      .attr('width', g('uS-W'))
      .attr('height',  g('unitFontSize'))
      .attr('opacity', 0.25)
      .attr('fill', '#68afca');
    // LegendSpace
    let groupLegend = selection.append('g')
      .attr('id', `guide-group-legend-${this.id}`);
    groupLegend.append('rect')
      .attr('id', `guide-legend-${this.id}`)
      .attr('x', g('lS-X'))
      .attr('y', g('lS-Y'))
      .attr('width', g('lS-W'))
      .attr('height', g('lS-H'))
      .attr('opacity', 0.5)
      .attr('fill', '#68afca');
    _.map(_.range(this.legendRows), row => {
      let groupRow = groupLegend.append('g')
        .attr('id', `guide-group-legend-row-${row}-${this.id}`);
      y = g('lS-Y') + g('legendMarginTop') + row * g('legendRowHeight') + row * g('legendVerticalSpacing');
      groupRow.append('rect')
        .attr('x', g('lS-X'))
        .attr('y', y)
        .attr('width', g('lS-W'))
        .attr('height', g('legendRowHeight'))
        .attr('opacity', 0.25)
        .attr('fill', '#68afca');
      _.map(_.range(this.legendColumns), column => {
        let groupColumn = groupLegend.append('g')
          .attr('id', `guide-group-legend-row-${row}-column-${column}-${this.id}`);
        w = (g('lS-W') - (this.legendColumns - 1) * g('legendHorizontalSpacing')) / this.legendColumns;
        x = g('lS-X') + column * w + column * g('legendHorizontalSpacing');
        groupRow.append('rect')
          .attr('x', x)
          .attr('y', y)
          .attr('width', w)
          .attr('height', g('legendRowHeight'))
          .attr('opacity', 0.25)
          .attr('fill', '#68afca');
      });
    });
    // Axes
    let groupAxes = selection.append('g')
      .attr('id', `guide-group-axes-${this.id}`);
    groupAxes.append('rect')
      .attr('id', `guide-axis-y-${this.id}`)
      .attr('x', g('lSY-X'))
      .attr('y', g('lSY-Y'))
      .attr('width', g('lSY-W'))
      .attr('height', g('lSY-H'))
      .attr('opacity', 0.5)
      .attr('fill', '#e54048');
    // groupAxes.append('rect')
    //   .attr('id', `guide-axis-y-labels-${this.id}`)
    //   .attr('x', this.sizes['yAxisLabelWidth']['x'])
    //   .attr('y', 0)
    //   // .attr('y', this.sizes['yAxisLabelWidth']['y'])
    //   .attr('width', this.sizes['yAxisLabelWidth']['width'])
    //   // .attr('height', this.sizes['yAxisLabelWidth']['height'])
    //   .attr('height', g('height'))
    //   .attr('opacity', 0.25)
    //   .attr('fill', '#e54048');
    groupAxes.append('rect')
      .attr('id', `guide-axis-x-${this.id}`)
      .attr('x', g('lSX-X'))
      .attr('y', g('lSX-Y'))
      .attr('width', g('lSX-W'))
      .attr('height', g('lSX-H'))
      .attr('opacity', 0.5)
      .attr('fill', '#e54048');
    // Chart pane
    let groupChartPane = selection.append('g')
      .attr('id', `guide-group-chart-pane-${this.id}`);
    groupChartPane.append('rect')
      .attr('id', `guide-chart-pane-${this.id}`)
      .attr('x', g('cP-X'))
      .attr('y', g('cP-Y'))
      .attr('width', g('cP-W'))
      .attr('height', g('cP-H'))
      .attr('opacity', 0.5)
      .attr('fill', '#fac748');
    // Chart space
    let groupChartSpace = selection.append('g')
      .attr('id', `guide-group-chart-space-${this.id}`);
    groupChartSpace.append('rect')
      .attr('id', `guide-chart-space-${this.id}`)
      .attr('x', g('cS-X'))
      .attr('y', g('cS-Y'))
      .attr('width', g('cS-W'))
      .attr('height', g('cS-H'))
      .attr('opacity', 0.5)
      .attr('fill', '#fac748');
    // Balls
    let groupBalls = selection.append('g')
      .attr('id', `guide-group-balls-${this.id}`);
    if (this.indices.length - 1 === 0) {
      groupBalls.append('circle')
        .attr('cx', this.scaleX(0))
        .attr('cy', this.scaleY((g('maxY') - g('minY')) / 2))
        .attr('r', 5)
        .attr('fill', '#3d6d7f')
        .attr('opacity', 0.5);
    } else {
      incr = (g('maxY') - g('minY')) / (this.indices.length - 1);
      // console.log(incr);
      _.map(this.indices, i => groupBalls.append('circle')
        .attr('cx', this.scaleX(i))
        .attr('cy', this.scaleY(g('minY') + i * incr))
        .attr('r', 5)
        .attr('fill', '#3d6d7f')
        .attr('opacity', 0.5));
    }
    // Bars
    let groupBars = selection.append('g')
      .attr('id', `guide-group-bars-${this.id}`);
    x = g('cS-X');
    for (let i = 0; i < g('categories'); i++) {
      groupBars.append('rect')
        .attr('x', x - g('categoryWidth') / 2)
        .attr('y', g('cS-Y'))
        .attr('width', g('categoryWidth'))
        .attr('height', g('cS-H'))
        .attr('fill', g('cS-H'))
        .attr('fill', '#3d6d7f')
        .attr('opacity', 0.25);
      xx = x - g('categoryWidth') / 2;
      for (let ii = 0; ii < g('subCategories'); ii++) {
        groupBars.append('rect')
          .attr('x', xx)
          .attr('y', g('cS-Y'))
          .attr('width', g('subCategoryWidth'))
          .attr('height', g('cS-H'))
          .attr('fill', g('cS-H'))
          .attr('fill', '#3d6d7f')
          .attr('opacity', 0.25);
        xx = xx + g('subCategoryWidth') + g('subCategorySpaceWidth');
      }
      x = x + g('cS-W') / (g('categories') - 1);
    }
  }

  public drawBackground(selection) {
    let g = (k): any => this.values[k];
    selection.append('rect')
      .attr('x', 0)
      .attr('y', 0)
      .attr('width', g('width'))
      .attr('height', g('height'))
      .attr('fill', g('backgroundColor') || 'white')
      .attr('stroke', 'none');
  }
  public drawTitleText(selection) {
    let g = (k): any => this.values[k];
    let singleReturn = /\r\n|\n|\f/g;

    let titleLines = g('titleText').split(singleReturn);
    let tn = titleLines.length;
    let y = g('tS-Y');
    if (g('titleText').length !== 0) {
      for (let i = 0; i < tn; i++) {
        y = i === 0 ? y + g('titleFontSize') : y + g('titleLineHeight') * g('titleFontSize');
        selection.append('text')
          .attr('x', g('tS-X'))
          .attr('y', y)
          .attr('text-anchor', 'start')
          .attr('font-family', g('fontFamily') || g('titleFontFamily'))
          .attr('font-size', g('titleFontSize'))
          .attr('font-weight', g('titleFontWeight'))
          .attr('fill', g('titleFontColor') || g('fontColor'))
          .text(titleLines[i]);
      }
    }
    let subTitleLines = g('subTitleText').split(singleReturn);
    let stn = subTitleLines.length;
    y = y + g('titleMarginBottom');
    if (g('subTitleText').length !== 0) {
      for (let i = 0; i < stn; i++) {
        y = i === 0 ? y + g('subTitleFontSize') : y + g('subTitleLineHeight') * g('subTitleFontSize');
        selection.append('text')
          .attr('x', g('tS-X'))
          .attr('y', y)
          .attr('text-anchor', 'start')
          .attr('font-family', g('fontFamily') || g('subTitleFontFamily'))
          .attr('font-size', g('subTitleFontSize'))
          .attr('font-weight', g('subTitleFontWeight'))
          .attr('fill', g('subTitleFontColor') || g('fontColor'))
          .text(subTitleLines[i]);
      }
    }
  }

  public drawUnitText(selection) {
    let g = (k): any => this.values[k];
    if (g('unitText')) {
      selection.append('text')
        .attr('x', g('cP-X') - g('yAxisLabelOffset') - this.yAxisLabelWidth)
        .attr('y', g('uS-Y') + g('unitMarginTop') + g('unitFontSize'))
        .attr('text-anchor', 'start')
        .attr('font-family', g('unitFontFamily') || g('fontFamily'))
        .attr('font-size', g('unitFontSize'))
        .attr('fill', g('unitFontColor') || g('fontColor'))
        .text(g('unitText'));
    }
  }

  public drawSourceText(selection) {
    let g = (k): any => this.values[k];
    if (g('sourceText')) {
      selection.append('text')
        .attr('x', g('lS-X'))
        .attr('y', g('lS-Y') + g('lS-H') + g('sourceMargin') + g('sourceFontSize'))
        .attr('text-anchor', 'start')
        .attr('font-family', g('sourceFontFamily') || g('fontFamily'))
        .attr('font-size', g('sourceFontSize'))
        .attr('fill', g('sourceFontColor') || g('fontColor'))
        .text(g('sourceText'));
    }
  }

  public drawXAxisBackground(selection) {
    let g = (k): any => this.values[k];
    selection.append('rect')
      .attr('x', g('lSX-X'))
      .attr('y', g('lSX-Y'))
      .attr('width', g('lSX-W'))
      .attr('height', g('lSX-H'))
      .attr('fill', g('labelSpaceBackgroundColor'))
      .attr('stroke', 'none');
  }

  public drawXAxisMarkers(selection) {
    let g = (k): any => this.values[k];
    let labels = _.map(_.drop(this.data, this.params['data']['columnLabelNumber']), list => list[0]);
    // console.log(labels);

    _.map(labels, (label, i) => {
      let x = this.scaleX(i);
      selection.append('line')
        .attr('x1', x)
        .attr('y1', g('lSX-Y') + g('xAxisMarkerOffset'))
        .attr('x2', x)
        .attr('y2', g('lSX-Y') + g('xAxisMarkerLength') + g('xAxisMarkerOffset'))
        .attr('stroke', g('xAxisLineStrokeColor') || g('labelSpaceLineStrokeColor'))
        .attr('stroke-width', g('xAxisMarkerWidth'));
      selection.append('text')
        .attr('x', x)
        .attr('y', g('lSX-Y') + g('xAxisLabelOffset'))
        .attr('text-anchor', 'middle')
        .attr('font-family', g('xAxisLabelFontFamily') || g('fontFamily'))
        .attr('font-size', g('xAxisLabelFontSize'))
        .attr('fill', g('xAxisLabelFontColor') || g('fontColor'))
        .text(label);
    });
  }

  public drawXAxisMarkersMultiPeriod(selection) {
    let g = (k): any => this.values[k];
    let labels = _.map(_.drop(this.data, this.params['data']['columnLabelNumber']), list => _.take(list, (this.params['data']['rowLabelNumber'])));
    // console.log(labels);
    let n = labels.length;
    let step = g('cS-W') / n;

    let yearStart = 0;
    let yearEnd = 0;
    let totalStep = 0;
    let lastYearLabelIndex = null;

    let drawYearStep = (position, year) => {
      selection.append('line')
        .attr('data-year-marker', year)
        .attr('x1', g('cS-X') + position)
        .attr('y1', g('lSX-Y') + g('xAxisMarkerOffset'))
        .attr('x2', g('cS-X') + position)
        .attr('y2', g('lSX-Y') + g('xAxisYearMarkerLength') + g('xAxisMarkerOffset'))
        .attr('stroke', g('xAxisLineStrokeColor') || g('labelSpaceLineStrokeColor'))
        .attr('stroke-width', g('xAxisYearMarkerWidth'));
    }

    let drawPeriodStep = (position, period) => {
      selection.append('line')
        .attr('data-period-marker', period)
        .attr('x1', g('cS-X') + position)
        .attr('y1', g('lSX-Y') + g('xAxisMarkerOffset'))
        .attr('x2', g('cS-X') + position)
        .attr('y2', g('lSX-Y') + g('xAxisMarkerLength') + g('xAxisMarkerOffset'))
        .attr('stroke', g('xAxisLineStrokeColor') || g('labelSpaceLineStrokeColor'))
        .attr('stroke-width', g('xAxisMarkerWidth'));
    }

    let drawYearLabel = (start, end, year, index, firstOrLast) => {
      let xAxisShowYears = g('xAxisShowYears')['value'];
      let strippedYear = year.replace(/[^\d.-]/g, '');
      let castYear = parseInt(strippedYear, 10);
      let isInt = !isNaN(castYear);
      let showYear;

      if (isInt) {
        switch (xAxisShowYears) {
          case 'all':
            showYear = true;
            break;
          case 'even':
            showYear = (castYear % 2 === 0);
            break;
          case 'odd':
            showYear = (castYear % 2 !== 0);
            break;
          case 'five':
            showYear = (castYear % 5 === 0);
            break;
          case 'skip 1':
            showYear = lastYearLabelIndex === null || lastYearLabelIndex < index - 1;
            break;
          case 'skip 2':
            showYear = lastYearLabelIndex === null || lastYearLabelIndex < index - 2;
            break;
          case 'firstAndLast':
            showYear = firstOrLast;
            break;
          default:
            showYear = true;
            break;
        }

        if (showYear) {
          lastYearLabelIndex = index;
        }
      }

      selection.append('text')
        .attr('data-year-label', year)
        .attr('x', g('cS-X') + start + (end - start) / 2)
        .attr('y', g('lSX-Y') + g('xAxisYearOffset'))
        .attr('text-anchor', 'middle')
        .attr('font-family', g('xAxisLabelFontFamily') || g('fontFamily'))
        .attr('font-size', g('xAxisYearFontSize'))
        .attr('fill', g('xAxisLabelFontColor') || g('fontColor'))
        .text(showYear || (g('xAxisForceFirstAndLastYear') && firstOrLast) ? year : '');
    }

    let drawPeriodLabel = (start, end, period) => {
      let showPeriod = g('xAxisShowPeriod');
      selection.append('text')
        .attr('data-period-label', period)
        .attr('x', g('cS-X') + start + (end - start) / 2)
        .attr('y', g('lSX-Y') + g('xAxisLabelOffset'))
        .attr('text-anchor', 'middle')
        .attr('font-family', g('xAxisLabelFontFamily') || g('fontFamily'))
        .attr('font-size', g('xAxisLabelFontSize'))
        .attr('fill', g('xAxisLabelFontColor') || g('fontColor'))
        .text(showPeriod ? period : '');
    }

    let yearIndex = 0;
    for (let i = 0; i < n; i++) {
      let label = labels[i];
      let year = label[0];
      let period = label[1];

      let nextYear = (labels[i + 1] || [])[0];
      let nextStep = totalStep + step;
      if (i === 0) {
        // Start with a long marker
        drawYearStep(totalStep, year);
      }
      if (nextYear === null || nextYear === undefined || nextYear !== year) {
        // If the next year is undefined or
        // different from the current year, draw a long marker
        drawYearStep(nextStep, nextYear);
        drawYearLabel(yearStart, nextStep, year, yearIndex, yearIndex === 0 || nextYear === null || nextYear === undefined);
        drawPeriodLabel(totalStep, nextStep, period);
        yearIndex += 1;
        yearStart = totalStep + step;
      } else {
        drawPeriodStep(nextStep, period);
        drawPeriodLabel(totalStep, nextStep, period);
      }
      totalStep = totalStep + step;
    }
  }

  public drawYAxisSteps(selection) {
    let g = (k): any => this.values[k];
    let minY = g('minY'), maxY = g('maxY'), stepY = g('stepY');
    let y = minY;
    let i = 0;

    let textGroup = selection.append('g');

    while (minY < maxY && y <= maxY && i <= 1000) {
      if (i === 1000) {
        console.log(`Too many Y-axis markers, no more than 1000 allowed.`)
      };
      if (y !== 0 || true) {
        if (i !== 0) {
          selection.append('line')
            .attr('x1', g('cP-X'))
            .attr('y1', this.scaleY(y))
            .attr('x2', g('cP-X') + g('cP-W'))
            .attr('y2', this.scaleY(y))
            .attr('stroke', g('yAxisMarkerStrokeColor'))
            .attr('stroke-width', g('yAxisMarkerStrokeWidth'));
        }
        textGroup.append('text')
          .attr('x', g('cP-X') - g('yAxisLabelOffset'))
          .attr('y', this.scaleY(y))
          .attr('text-anchor', 'end')
          .attr('font-family', g('yAxisLabelFontFamily') || g('fontFamily'))
          .attr('font-size', g('yAxisLabelFontSize'))
          .attr('dominant-baseline', 'middle')
          .attr('fill', g('fontColor'))
          .text(this.formatNumberDecimals(y, g('yAxisLabelDecimals'), g('formatLocale')['value']));
      }
      y = y + stepY;
      i++;
    }

    // Draw the 0 line
    // disabled on purpose, the Fins seem to not do the wiggly line and stuff
    // if (false) {
    //   selection.append('line')
    //     .attr('x1', g('cP-X'))
    //     .attr('y1', this.scaleY(this.zeroY))
    //     .attr('x2', g('cP-X') + g('cP-W'))
    //     .attr('y2', this.scaleY(this.zeroY))
    //     .attr('stroke', g('yAxisMarkerStrokeColor'))
    //     .attr('stroke-width', g('labelSpaceLineStrokeWidth'));
    //   textGroup.append('text')
    //     .attr('x', g('cP-X') - g('yAxisLabelOffset'))
    //     .attr('y', this.scaleY(this.zeroY))
    //     .attr('text-anchor', 'end')
    //     .attr('font-family', g('yAxisLabelFontFamily') || g('fontFamily'))
    //     .attr('font-size', g('yAxisLabelFontSize'))
    //     .attr('dominant-baseline', 'middle')
    //     .attr('fill', g('fontColor'))
    //     .text(this.formatNumberDecimals(0, g('yAxisLabelDecimals'), g('formatLocale')['value']));
    // }
    return textGroup;
  }

  public drawYAxisStepsForMeasurement(selection) {
    // NB - if the above method changes, remember to also change this one!
    let g = (k): any => this.values[k];
    let minY = g('minY'), maxY = g('maxY'), stepY = g('stepY');
    let y = minY;
    let i = 0;

    let textGroup = selection.append('g');

    while (minY < maxY && y <= maxY && i <= 1000) {
      if (i === 1000) {
        console.log(`Too many Y-axis markers, no more than 1000 allowed.`)
      };
      if (y !== 0 || true) {
        textGroup.append('text')
          .attr('x', 0)
          .attr('y', 0)
          .attr('text-anchor', 'end')
          .attr('font-family', g('yAxisLabelFontFamily') || g('fontFamily'))
          .attr('font-size', g('yAxisLabelFontSize'))
          .attr('dominant-baseline', 'middle')
          .attr('fill', g('fontColor'))
          .text(this.formatNumberDecimals(y, g('yAxisLabelDecimals'), g('formatLocale')['value']));
      }
      y = y + stepY;
      i++;
    }
    return textGroup;
  }

  public drawLines(selection) {
    let g = (k): any => this.values[k];

    let values = this.getDataValues();
    let transposed = _.unzip(values);
    let colors = g('reverseColors') ? _.clone(this.colors).reverse() : _.clone(this.colors);

    let drawLine = (list, index) => {
      if (list.length > 0) {
        let group = selection.append('g')
          .attr('id', `group-lines-${index}-${this.id}`);
        let fx = (d, i) => this.scaleX(i);
        let fy = (d, i) => this.scaleY(d);
        let line = d3.line().x(fx).y(fy);
        let lineString = line(list);

        let path = group.append('path')
          .attr('id', `line-${index}-${this.id}`)
          .attr('d', lineString)
          .attr('fill', 'none')
          .attr('stroke', colors[index])
          .attr('stroke-width', g('lineStrokeWidth'));
        if (g('lineJoin')) {
          path.attr('stroke-linejoin', g('lineJoin')['value'])
        }
      }
    };

    _.map(transposed, drawLine);
  }

  public drawLegend(selection, marker: 'line' | 'rect') {
    let g = (k): any => this.values[k];
    let colors = g('reverseColors') ? _.clone(this.colors).reverse() : _.clone(this.colors);

    let drawLegendItem = (row, column, i) => {
      if (i <= this.legendLabels.length - 1) {
        let y = g('lS-Y') + g('legendMarginTop') + row * g('legendRowHeight') + row * g('legendVerticalSpacing');
        let w = (g('lS-W') - (this.legendColumns - 1) * g('legendHorizontalSpacing')) / this.legendColumns;
        let mw = g('legendMarkerWidth');
        let mm = g('legendMarkerMargin');
        let tw = w - mw - mm;
        let x = g('lS-X') + column * w + column * g('legendHorizontalSpacing');
        let label = this.legendLabels[i];
        let lineFactor = 0.6;
        let group = selection.append('g')
          .attr('id', `legend-row-${row}-column-${column}-${this.id}`);
        if (marker === 'line') {
          group.append('line')
            .attr('x1', x)
            .attr('y1', y + lineFactor * g('legendRowHeight'))
            .attr('x2', x + mw)
            .attr('y2', y + lineFactor * g('legendRowHeight'))
            .attr('stroke', colors[i])
            .attr('stroke-width', g('lineStrokeWidth'));
        } else if (marker === 'rect') {
          group.append('rect')
            .attr('x', x)
            .attr('y', y + g('legendRowHeight') - (g('legendMarkerHeight') || g('legendRowHeight')))
            .attr('width', mw)
            .attr('height', g('legendMarkerHeight') || g('legendRowHeight'))
            .attr('fill', colors[i]);
        }
        group.append('text')
          .attr('x', x + mw + mm)
          .attr('y', y + g('legendRowHeight'))
          .attr('text-anchor', 'start')
          .attr('font-family', g('legendFontFamily') || g('fontFamily'))
          .attr('font-size', g('legendFontSize'))
          .attr('fill', g('fontColor'))
          .text(label);
      }
    }

    if (g('legendWrapping')['value'] === 'right') {
      let i = 0;
      _.map(_.range(this.legendRows), row => {
        _.map(_.range(this.legendColumns), column => {
          drawLegendItem(row, column, i);
          i++;
        });
      });
    } else if (g('legendWrapping')['value'] === 'down') {
      let i = 0;
      _.map(_.range(this.legendColumns), column => {
        _.map(_.range(this.legendRows), row => {
          drawLegendItem(row, column, i);
          i++;
        });
      });
    } else {
      console.log(`Unknown legendWrapping: ${g('legendWrapping')['value']}`);
    }
  }

}



// WEBPACK FOOTER //
// ./src/app/animations/demo-tilastokeskus-line/demo-tilastokeskus-line.component.ts